import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase.js';
import GoogleAnalytics from '../../analyticsComponents/GoogleAnalytics.js';
import Spinner from '../../utils/Spinner';
import Toast from '../../utils/Toast';
import logger from '../../utils/logger';
import { useUser } from '../../utils/UserContext';
import useModerators, { moderatorName } from '../../utils/useModerators';
import {
  ALL_GRADES,
  SUBJECTS,
  gradeLabel,
  parseScope,
  scopeKey,
  scopeLabel,
} from '../../utils/catalogue';

/**
 * REQ-01 — administrators assign moderators to subjects, then grades
 * (decision D1). A moderator may hold several scopes and several moderators
 * may share one; the first to act on a resource completes it.
 */

// Keep scopes tidy: "Subject/*" replaces that subject's single-grade scopes.
const normaliseScopes = (scopes) => {
  const wholeSubjects = new Set(
    scopes.map(parseScope).filter((s) => s.grade === ALL_GRADES).map((s) => s.subject)
  );
  return Array.from(new Set(scopes))
    .filter((s) => {
      const { subject, grade } = parseScope(s);
      return grade === ALL_GRADES || !wholeSubjects.has(subject);
    })
    .sort((a, b) => scopeLabel(a).localeCompare(scopeLabel(b)));
};

const ScopeEditor = ({ onAdd }) => {
  const [subject, setSubject] = useState('');
  const [grades, setGrades] = useState([]);
  const [allGrades, setAllGrades] = useState(true);

  const subjectInfo = SUBJECTS.find((s) => s.subject === subject);

  const toggleGrade = (grade) =>
    setGrades((prev) => (prev.includes(grade) ? prev.filter((g) => g !== grade) : [...prev, grade]));

  const add = () => {
    if (!subject) return;
    const scopes = allGrades ? [scopeKey(subject)] : grades.map((g) => scopeKey(subject, g));
    if (scopes.length === 0) return;
    onAdd(scopes);
    setSubject('');
    setGrades([]);
    setAllGrades(true);
  };

  return (
    <div className="rounded-lg border border-dashed border-gray-300 p-3 space-y-3">
      <div className="flex flex-col sm:flex-row gap-2">
        <select
          value={subject}
          onChange={(e) => { setSubject(e.target.value); setGrades([]); setAllGrades(true); }}
          className="flex-1 py-1.5 px-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
        >
          <option value="">1. Choose a subject…</option>
          {SUBJECTS.map((s) => (
            <option key={s.subject} value={s.subject}>{s.label}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={add}
          disabled={!subject || (!allGrades && grades.length === 0)}
          className="px-3 py-1.5 text-sm font-medium text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:bg-brand-300 disabled:cursor-not-allowed transition-colors"
        >
          Add scope
        </button>
      </div>
      {subjectInfo && (
        <div>
          <p className="text-xs font-medium text-gray-500 mb-1.5">2. Choose grades</p>
          <label className="inline-flex items-center gap-1.5 text-xs text-gray-700 mr-3 mb-1.5">
            <input type="checkbox" checked={allGrades} onChange={(e) => setAllGrades(e.target.checked)} />
            All grades offering {subjectInfo.label}
          </label>
          {!allGrades && (
            <div className="flex flex-wrap gap-1.5">
              {subjectInfo.grades.map((g) => (
                <label
                  key={g}
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs cursor-pointer ${
                    grades.includes(g) ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-200 text-gray-600'
                  }`}
                >
                  <input type="checkbox" className="sr-only" checked={grades.includes(g)} onChange={() => toggleGrade(g)} />
                  {gradeLabel(g)}
                </label>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ModeratorCard = ({ moderator, onSave }) => {
  const [editing, setEditing] = useState(false);
  const scopes = moderator.moderationScopes || [];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-gray-900 truncate">{moderatorName(moderator)}</p>
          <p className="text-xs text-gray-500 truncate">{moderator.email}</p>
        </div>
        <button
          type="button"
          onClick={() => setEditing((v) => !v)}
          className="shrink-0 text-xs font-medium text-brand-600 hover:text-brand-700"
        >
          {editing ? 'Done' : 'Edit scopes'}
        </button>
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {scopes.length === 0 && (
          <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
            No scopes — sees no moderation queue
          </span>
        )}
        {scopes.map((s) => (
          <span key={s} className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2.5 py-0.5 text-xs font-medium text-brand-700">
            {scopeLabel(s)}
            {editing && (
              <button
                type="button"
                aria-label={`Remove ${scopeLabel(s)}`}
                onClick={() => onSave(moderator, scopes.filter((x) => x !== s))}
                className="text-brand-400 hover:text-red-500"
              >
                ✕
              </button>
            )}
          </span>
        ))}
      </div>

      {editing && (
        <div className="mt-3">
          <ScopeEditor onAdd={(added) => onSave(moderator, [...scopes, ...added])} />
        </div>
      )}

      {moderator.moderationScopesUpdatedAt && (
        <p className="mt-3 text-[11px] text-gray-400">
          Last changed {moderator.moderationScopesUpdatedAt.toDate?.().toLocaleString()}
          {moderator.moderationScopesUpdatedBy ? ` by ${moderator.moderationScopesUpdatedBy}` : ''}
        </p>
      )}
    </div>
  );
};

const ModeratorAssignmentsPage = () => {
  const { currentUser } = useUser();
  const { moderators, coverage, loading, error } = useModerators(true);
  const [search, setSearch] = useState('');
  const [gapsOnly, setGapsOnly] = useState(false);
  const [toast, setToast] = useState({ visible: false, message: '', variant: 'success' });

  const saveScopes = async (moderator, scopes) => {
    try {
      await updateDoc(doc(db, 'users', moderator.id), {
        moderationScopes: normaliseScopes(scopes),
        moderationScopesUpdatedAt: Timestamp.now(),
        moderationScopesUpdatedBy: currentUser?.email || currentUser?.uid || 'admin',
      });
      setToast({ visible: true, message: `Scopes updated for ${moderatorName(moderator)}.`, variant: 'success' });
    } catch (err) {
      logger.error('Error saving moderator scopes:', err);
      setToast({ visible: true, message: 'Could not save scopes. Please try again.', variant: 'error' });
    }
  };

  const filteredModerators = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return moderators;
    return moderators.filter(
      (m) =>
        moderatorName(m).toLowerCase().includes(s) ||
        (m.email || '').toLowerCase().includes(s) ||
        (m.moderationScopes || []).some((scope) => scopeLabel(scope).toLowerCase().includes(s))
    );
  }, [moderators, search]);

  const coverageRows = useMemo(
    () =>
      SUBJECTS.map((s) => ({
        ...s,
        cells: s.grades.map((g) => ({ grade: g, moderators: coverage[`${g}/${s.subject}`] || [] })),
      })).filter((row) => !gapsOnly || row.cells.some((c) => c.moderators.length === 0)),
    [coverage, gapsOnly]
  );

  const totalScopes = SUBJECTS.reduce((n, s) => n + s.grades.length, 0);
  const uncovered = Object.values(coverage).filter((list) => list.length === 0).length;
  const withoutScopes = moderators.filter((m) => !(m.moderationScopes || []).length).length;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <GoogleAnalytics page="ModeratorAssignmentsPage" eventType="moderatorAssignments_page" />
      <Toast
        message={toast.message}
        variant={toast.variant}
        visible={toast.visible}
        onDismiss={() => setToast((t) => ({ ...t, visible: false }))}
      />

      <div className="mb-6">
        <div className="flex items-center gap-3">
          <span className="inline-block px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">Admin</span>
          <h1 className="text-2xl font-bold text-gray-900">Moderator Assignments</h1>
        </div>
        <p className="mt-1 text-sm text-gray-500 max-w-3xl">
          Assign each moderator to a subject, then to all or some of its grades. Moderators only see, approve and
          receive notifications for resources in their scopes. Administrators always see every queue. To make
          someone a moderator, use <Link to="/role-assign" className="text-brand-600 hover:underline">Assign Roles</Link>.
        </p>
      </div>

      {error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Moderators could not be loaded ({error}).
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500">Moderators</p>
          <p className="text-2xl font-bold text-gray-900">{moderators.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500">Moderators without scopes</p>
          <p className={`text-2xl font-bold ${withoutScopes ? 'text-amber-600' : 'text-gray-900'}`}>{withoutScopes}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500">Subject/grade scopes with no moderator</p>
          <p className={`text-2xl font-bold ${uncovered ? 'text-amber-600' : 'text-gray-900'}`}>
            {uncovered} <span className="text-sm font-normal text-gray-400">of {totalScopes}</span>
          </p>
        </div>
      </div>

      {loading ? (
        <Spinner size="lg" text="Loading moderators…" />
      ) : (
        <>
          <section className="mb-10">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Moderators</h2>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email or scope"
                className="flex-1 min-w-[220px] px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
            {filteredModerators.length === 0 ? (
              <p className="text-sm text-gray-500">No moderators found.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredModerators.map((m) => (
                  <ModeratorCard key={m.id} moderator={m} onSave={saveScopes} />
                ))}
              </div>
            )}
          </section>

          <section>
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <h2 className="text-lg font-semibold text-gray-900">Coverage</h2>
              <label className="inline-flex items-center gap-1.5 text-sm text-gray-600">
                <input type="checkbox" checked={gapsOnly} onChange={(e) => setGapsOnly(e.target.checked)} />
                Only subjects with gaps
              </label>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              Uploads into an amber scope have no moderator and are visible to administrators only.
            </p>
            <div className="rounded-xl border border-gray-200 bg-white divide-y divide-gray-100">
              {coverageRows.map((row) => (
                <div key={row.subject} className="px-4 py-3 flex flex-col md:flex-row md:items-center gap-2">
                  <div className="md:w-64 shrink-0 text-sm font-medium text-gray-800">{row.label}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {row.cells.map((c) => (
                      <span
                        key={c.grade}
                        title={c.moderators.length ? c.moderators.map(moderatorName).join(', ') : 'No moderator assigned'}
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          c.moderators.length ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {gradeLabel(c.grade)}
                        {c.moderators.length > 0 && <span className="ml-1 text-emerald-500">· {c.moderators.length}</span>}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
              {coverageRows.length === 0 && (
                <p className="px-4 py-6 text-sm text-gray-500 text-center">Every subject and grade has a moderator.</p>
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default ModeratorAssignmentsPage;
